import { useCallback, useEffect, useRef, useState } from "react";
const DEFAULT_POLL_INTERVAL_MS = 3_000;
export interface UsePollingListenerOptions {
  /** How often to poll, in ms. Default: 3000 */
  pollIntervalMs?: number;
  /** Total timeout before giving up, in ms */
  timeoutMs: number;
  /** Error message shown when the listener times out */
  timeoutMessage: string;
  /**
   * Called every poll tick. Return `true` to signal detection
   * (stops polling and triggers onDetected). Return `false` to keep polling.
   */
  onPoll: () => Promise<boolean>;
  /** Called once when onPoll returns true */
  onDetected: () => void | Promise<void>;
}
export interface UsePollingListenerResult {
  isListening: boolean;
  /** Whole seconds remaining until timeout */
  secondsRemaining: number;
  error: string | null;
  startListening: () => void;
  stopListening: () => void;
  /** Whether a detection has occurred (for rendering success state) */
  detected: boolean;
}

/**
 * Shared hook that encapsulates the poll-interval + countdown-timer + timeout
 * pattern used by live-event-detection archetypes (GenericLiveListener,
 * ShopifyInventoryLive, SlackInteraction, etc.).
 */
export function usePollingListener({
  pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
  timeoutMs,
  timeoutMessage,
  onPoll,
  onDetected
}: UsePollingListenerOptions): UsePollingListenerResult {
  const [isListening, setIsListening] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(Math.ceil(timeoutMs / 1000));
  const [error, setError] = useState<string | null>(null);
  const [detected, setDetected] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isListeningRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Keep refs to the latest callbacks so the setInterval closure never goes stale
  const onPollRef = useRef(onPoll);
  const onDetectedRef = useRef(onDetected);
  useEffect(() => {
    onPollRef.current = onPoll;
  }, [onPoll]);
  useEffect(() => {
    onDetectedRef.current = onDetected;
  }, [onDetected]);
  const cleanup = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    intervalRef.current = null;
    timeoutRef.current = null;
    countdownRef.current = null;
    isListeningRef.current = false;
    isFetchingRef.current = false;
  }, []);
  const stopListening = useCallback(() => {
    cleanup();
    setIsListening(false);
  }, [cleanup]);

  // Cleanup on unmount
  useEffect(() => cleanup, [cleanup]);
  const startListening = useCallback(() => {
    cleanup();
    setIsListening(true);
    setDetected(false);
    setError(null);
    setSecondsRemaining(Math.ceil(timeoutMs / 1000));
    isListeningRef.current = true;
    const startTime = Date.now();

    // Countdown — store whole seconds, skip updates when unchanged
    countdownRef.current = setInterval(() => {
      const secs = Math.max(0, Math.ceil((timeoutMs - (Date.now() - startTime)) / 1000));
      setSecondsRemaining(prev => prev === secs ? prev : secs);
    }, 1000);

    // Poll interval — uses refs so callbacks are never stale
    intervalRef.current = setInterval(async () => {
      if (!isListeningRef.current || isFetchingRef.current) return;
      isFetchingRef.current = true;
      try {
        const isDetected = await onPollRef.current();
        if (!isListeningRef.current) return;
        if (isDetected) {
          setDetected(true);
          stopListening();
          await onDetectedRef.current();
        }
      } catch {
        // Continue polling on transient errors
      } finally {
        isFetchingRef.current = false;
      }
    }, pollIntervalMs);

    // Timeout
    timeoutRef.current = setTimeout(() => {
      stopListening();
      setError(timeoutMessage);
    }, timeoutMs);
  }, [cleanup, stopListening, pollIntervalMs, timeoutMs, timeoutMessage]);
  return {
    isListening,
    secondsRemaining,
    error,
    startListening,
    stopListening,
    detected
  };
}